// externals
import { useSelector } from 'react-redux';
import { useMemo } from 'react';

// store
import { vehicleDetailsSelectors } from '../store';

// utils
import {
    calculateProgress,
    calculateFilledDots,
    formatInServiceDate,
    formatMonths,
    formatMiles
} from '../components/WarrantySection/warrantyUtils';
import { calculateWarrantyStatus } from '../utils/warrantyUtils';
import {
    TOTAL_DOTS,
    VEHICLE_CONDITION_NEW,
    WARRANTY_IN_SERVICE_DATE_LABEL,
    WARRANTY_UNLIMITED_MILES_LABEL,
    WARRANTY_UNLIMITED_MILES_SENTINEL,
    WARRANTY_UNLIMITED_MILES_VALUE
} from '../constants';

export const useWarrantyStatus = () => {
    const rawWarranties = useSelector(vehicleDetailsSelectors.getWarranties);
    const inServiceDate = useSelector(vehicleDetailsSelectors.getInServiceDate);
    const condition = useSelector(vehicleDetailsSelectors.getCondition);
    const odometer = useSelector(vehicleDetailsSelectors.getOdometer);

    const warrantyData = rawWarranties ?? [];
    const isNewVehicle = condition?.toLowerCase() === VEHICLE_CONDITION_NEW;
    const hasInServiceDate = Boolean(inServiceDate);

    // Use 0 as odometer for NEW vehicles for warranty calculations
    const effectiveOdometer = isNewVehicle ? 0 : odometer;

    const shouldShowWarranties = (isNewVehicle || hasInServiceDate) && warrantyData.length > 0;
    const inServiceDateLabel =
        !isNewVehicle && inServiceDate ? WARRANTY_IN_SERVICE_DATE_LABEL(formatInServiceDate(inServiceDate)) : undefined;

    const warrantyDisplayItems = useMemo(
        () =>
            shouldShowWarranties
                ? warrantyData.map((item) => {
                      const months = item.categoryDetails.years ? Number(item.categoryDetails.years) * 12 : undefined;
                      // 'Unlimited' has no numeric value, so classify it before parsing rather than
                      // letting Number() turn it into NaN and share a branch with malformed data
                      const rawMiles = item.categoryDetails.miles?.trim();
                      const parsedMiles = rawMiles ? Number(rawMiles.replace(/,/g, '')) : NaN;
                      const isUnlimitedMiles =
                          rawMiles?.toLowerCase() === WARRANTY_UNLIMITED_MILES_VALUE ||
                          parsedMiles === WARRANTY_UNLIMITED_MILES_SENTINEL;
                      const miles = !isUnlimitedMiles && Number.isFinite(parsedMiles) ? parsedMiles : undefined;

                      const { status, isExpired, remainingMonths, remainingMiles } = calculateWarrantyStatus(
                          months,
                          miles,
                          inServiceDate,
                          effectiveOdometer
                      );

                      const timeProgress = calculateProgress(remainingMonths, months);
                      const milesProgress = calculateProgress(remainingMiles, miles);
                      const timeFilledDots = calculateFilledDots(timeProgress, TOTAL_DOTS);
                      const milesFilledDots = calculateFilledDots(milesProgress, TOTAL_DOTS);
                      // Coverage ends when either limit is reached, so both axes read as expired.
                      // Flipping the remaining value negative puts the existing formatters into
                      // their expired branch, rather than duplicating those labels here.
                      const expiredMonths = isExpired && remainingMonths != null ? -Math.abs(remainingMonths) : remainingMonths;
                      const expiredMiles = isExpired && remainingMiles != null ? -Math.abs(remainingMiles) : remainingMiles;

                      const formattedMonths = formatMonths(expiredMonths, months);
                      const formattedMiles = isUnlimitedMiles ? WARRANTY_UNLIMITED_MILES_LABEL : formatMiles(expiredMiles, miles);

                      return {
                          description: item.categoryKey,
                          status,
                          months,
                          miles,
                          remainingMonths,
                          remainingMiles,
                          isExpired,
                          formattedMonths,
                          formattedMiles,
                          timeFilledDots,
                          milesFilledDots
                      };
                  })
                : [],
        [shouldShowWarranties, warrantyData, inServiceDate, effectiveOdometer]
    );

    return {
        warranties: warrantyDisplayItems,
        shouldShowWarranties,
        inServiceDateLabel
    };
};
